import React, { useState, useEffect, useMemo } from 'react';
import {
  getDesignParameters,
  resolveParamValues,
  computeDerived,
  applyDerivedToProcesses,
  applyDesignParameters,
  isInventoryInSync,
  DESIGN_PARAM_VALUES_KEY,
} from '../utils/designParameters';
import { registerBridge, unregisterActions } from '../services/agent/actionBridge';
import CellDrawing, { hasCellGeometry } from './CellDrawing';
import SourcesButton from './SourcesButton';
import { isTemplateMode } from '../services/lcaCalculator';
import { summarizeProject, percentChange, isDefaultValue } from '../services/batpacDesign';

/**
 * Typed entry for one slider. Keeps a local draft while the user types (so a
 * half-typed "8" on the way to "82" is not clamped to the minimum), then
 * commits on blur or Enter, clamped to the slider's range.
 */
function ParamNumberBox({ def, value, onCommit, style }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const n = parseFloat(draft);
    if (!Number.isFinite(n)) { setDraft(String(value)); return; }
    // Clamp to the range but do not snap to the slider step: the step is for
    // coarse dragging, and a typed 82 µm should stay 82 µm.
    const clamped = Math.min(def.max, Math.max(def.min, Number(n.toFixed(4))));
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  return (
    <input
      type="number"
      inputMode="decimal"
      min={def.min}
      max={def.max}
      step={def.step}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      style={style}
      aria-label={`${def.label || 'value'} (type a value)`}
      title={`${def.min}–${def.max}, step ${def.step}`}
    />
  );
}

// Slider groups a block may declare. Anything else is an electrode setting.
const MATERIAL = 'material';
const CELL = 'cell';

/**
 * Electrode settings a block marks `primary` are shown when the panel opens;
 * the rest wait under "All settings". A block that marks none shows all of
 * its electrode settings up front.
 */
function groupParams(params) {
  const groups = { [MATERIAL]: [], primary: [], more: [], [CELL]: [] };
  const entries = Object.entries(params || {});
  const anyPrimary = entries.some(([, def]) => def?.primary && !def?.group);
  for (const entry of entries) {
    const def = entry[1] || {};
    if (groups[def.group]) groups[def.group].push(entry);
    else if (def.primary || !anyPrimary) groups.primary.push(entry);
    else groups.more.push(entry);
  }
  return groups;
}

function hasSubInventory(project) {
  return Object.values(project?.processes || {})
    .some(proc => (proc.lci_inputs || []).some(input => input?.sub_inventory));
}

/**
 * DesignParametersPanel
 *
 * Renders whatever design parameters a template declares and recalculates the
 * LCI quantities they drive. Nothing here is specific to a chemistry or a
 * process: the sliders, the formulas and the inventory rows they write to all
 * come from the template's `design_parameters` block (see
 * `src/utils/designParameters.js`). The SeaLion oCVD coating template is one
 * instance of that schema, not a special case.
 *
 * Open, it shows the design's Wh/kg, carbon and cost per kWh (live from the
 * slider positions, against the default design, before Apply) and the
 * `primary` sliders. "All settings" adds the other electrode sliders, the
 * `group: "material"` sliders (the cathode's own capacity, voltage and
 * density, each tagged "BatPaC" at its default and "yours" once changed), the
 * `group: "cell"` sliders with the cell picture, and the calculated amounts.
 * The design itself is usually picked before the project exists, so none of
 * this is needed to read the results.
 *
 * Props:
 *   project         – current project object
 *   onUpdateProject – callback invoked with the project carrying new quantities
 */
const DesignParametersPanel = ({ project, onUpdateProject }) => {
  const block = useMemo(() => getDesignParameters(project), [project]);

  // Signature of the parameter set we are currently showing — when the user
  // switches project or template, reset the sliders instead of carrying the
  // previous project's values over.
  const signature = useMemo(() => {
    if (!block) return '';
    return JSON.stringify([
      project?.project_name ?? '',
      Object.keys(block.params),
      project?.[DESIGN_PARAM_VALUES_KEY] ?? null,
    ]);
  }, [block, project]);

  const [values, setValues] = useState(() => (block ? resolveParamValues(block, project) : {}));
  const [lastSignature, setLastSignature] = useState(signature);
  // Collapsed by default: open, the sliders take most of the Results panel.
  const [open, setOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);

  // Let the walkthrough open the panel it spotlights, only while it is mounted.
  useEffect(() => {
    registerBridge({ actions: { openDesignPanel: () => { setOpen(true); return { ok: true }; } } });
    return () => unregisterActions(['openDesignPanel']);
  }, []);

  useEffect(() => {
    if (signature === lastSignature) return;
    setLastSignature(signature);
    setValues(block ? resolveParamValues(block, project) : {});
  }, [signature, lastSignature, block, project]);

  // Sliders away from the applied design. Moving one back, or Reset to the
  // design already applied, leaves nothing to apply.
  const dirty = useMemo(() => {
    if (!block) return false;
    const applied = resolveParamValues(block, project);
    return Object.entries(block.params).some(([key, def]) => (
      !isDefaultValue({ ...def, default: applied[key] }, values[key] ?? def.default)));
  }, [block, project, values]);

  const { derived, errors, unresolved, inSync } = useMemo(() => {
    if (!block) return { derived: {}, errors: [], unresolved: [], inSync: true };
    const computed = computeDerived(block, values);
    const { unresolved: missing } = applyDerivedToProcesses(project, block, computed.values);
    return {
      derived: computed.values,
      errors: computed.errors,
      unresolved: missing,
      inSync: isInventoryInSync(project, block, computed.values),
    };
  }, [block, values, project]);

  // Carbon and cost per kWh at the slider positions and at the default design.
  // Linked sub-inventories are resolved only by a full run, so a project with
  // any gets no preview rather than a wrong one.
  const canPreview = useMemo(
    () => !!block && isTemplateMode(project) && !hasSubInventory(project),
    [block, project],
  );
  const preview = useMemo(
    () => (canPreview ? summarizeProject(applyDesignParameters(project, block, values).project) : null),
    [canPreview, project, block, values],
  );
  const atDefaults = useMemo(() => {
    if (!canPreview) return null;
    const defaults = Object.fromEntries(Object.entries(block.params).map(([k, def]) => [k, def.default]));
    return summarizeProject(applyDesignParameters(project, block, defaults).project);
  }, [canPreview, project, block]);

  if (!block) return null;

  // Everything a drawing may read: the block's constants, the sliders, and what they derive.
  const scope = { ...block.constants, ...values, ...derived };
  const hasDrawing = hasCellGeometry(scope);
  const groups = groupParams(block.params);

  const handleChange = (key, raw) => {
    const next = parseFloat(raw);
    if (!Number.isFinite(next)) return;
    setValues(prev => ({ ...prev, [key]: next }));
  };

  const handleReset = () => {
    const defaults = {};
    for (const [key, def] of Object.entries(block.params)) defaults[key] = def.default;
    setValues(defaults);
  };

  const handleApply = () => {
    if (!onUpdateProject || !project) return;
    onUpdateProject(applyDesignParameters(project, block, values).project);
  };

  const canApply = dirty || !inSync;

  const formatValue = (value) => {
    if (!Number.isFinite(value)) return '—';
    if (value !== 0 && Math.abs(value) < 1e-3) return value.toExponential(2);
    return String(Number(value.toPrecision(4)));
  };

  const perUnit = preview?.perKwh ? 'kWh' : 'kg';
  const carbonUnit = `kg CO₂e/${perUnit}`;
  const costUnit = `$/${perUnit}`;

  // One-line state for the collapsed header.
  const headline = block.display.find(d => d.emphasis);
  const headlineText = headline && Number.isFinite(derived[headline.key])
    ? `${formatValue(derived[headline.key])}${headline.unit ? ` ${headline.unit}` : ''}`
    : null;
  const carbonText = preview ? `${preview.carbon.toFixed(1)} ${carbonUnit}` : null;
  let status;
  if (dirty) status = 'Changes not applied';
  else if (!inSync) status = 'Not applied · template quantities in use';
  else status = ['Applied', headlineText, carbonText].filter(Boolean).join(' · ');

  // Styles
  const sectionStyle = {
    background: '#f0f7ff',
    border: '1px solid #c8ddf5',
    borderRadius: '8px',
    padding: open ? '14px 16px' : '8px 14px',
    marginBottom: '12px',
  };

  const toggleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    minWidth: 0,
  };
  const chevronStyle = { fontSize: '9px', color: '#1a56a0', width: '10px', flexShrink: 0 };
  const titleStyle = { fontSize: open ? '15px' : '13px', fontWeight: 600, color: '#1a56a0' };
  const statusStyle = { fontSize: '11px', color: canApply ? '#8a5a00' : '#5a6b7d' };

  // Top: what this is, then the numbers the design gives.
  const introStyle = { marginBottom: '12px' };
  const figuresStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px 28px', margin: '10px 0 0 18px' };
  const bigNumberStyle = { fontSize: '26px', fontWeight: 600, color: '#1a56a0', fontFamily: 'monospace', lineHeight: 1 };
  const figureUnitStyle = { fontSize: '13px', color: '#5a6b7d' };

  // Below: what you set, and what it gives. Two columns when there is room
  // (never three: the tagged material rows need the width), one when not.
  const cardsStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, max(330px, calc((100% - 12px) / 2))), 1fr))',
    gap: '12px',
    alignItems: 'start',
  };
  const cardStyle = { background: '#fff', border: '1px solid #d6e4f5', borderRadius: '8px', padding: '12px 14px', minWidth: 0 };
  const cardTitleStyle = {
    fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
    color: '#5a6b7d', margin: '0 0 10px',
  };

  // One grid per card, so the columns line up whatever the label or unit length.
  // Tagged rows put the tag beside the unit, wrapping under it when narrow.
  const slidersStyle = (tagged) => ({
    display: 'grid',
    gridTemplateColumns: tagged ? 'minmax(70px, 120px) minmax(40px, 1fr) 72px auto' : 'minmax(84px, 120px) minmax(60px, 1fr) 72px auto',
    alignItems: 'center',
    gap: '8px 8px',
  });
  const labelStyle = { fontSize: '12px', color: '#444', fontWeight: 500, lineHeight: 1.25 };
  const valueBoxStyle = {
    fontSize: '12px', fontFamily: 'monospace', color: '#1a56a0', fontWeight: 600, textAlign: 'right',
    width: '100%', boxSizing: 'border-box', padding: '2px 4px',
    border: '1px solid #d0d8e2', borderRadius: '3px', background: '#fff',
  };
  const unitStyle = { fontSize: '11px', color: '#888', whiteSpace: 'nowrap' };
  const tagStyle = (yours) => ({
    fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: '999px', whiteSpace: 'nowrap',
    ...(yours
      ? { background: '#e8f0fe', color: '#1a73e8', border: '1px solid #c6dafc' }
      : { background: '#f8f9fa', color: '#5f6368', border: '1px solid #e8eaed' }),
  });
  const derivedRowStyle = {
    display: 'flex', justifyContent: 'space-between', gap: '12px',
    fontSize: '12px', color: '#444', padding: '3px 0', borderBottom: '1px solid #f0f3f7',
  };
  const warningStyle = {
    fontSize: '11px',
    color: '#8a5a00',
    background: '#fff8e6',
    border: '1px solid #f0dca8',
    borderRadius: '4px',
    padding: '6px 8px',
    marginTop: '8px',
  };

  const toggle = (
    <button type="button" style={toggleStyle} onClick={() => setOpen(o => !o)} aria-expanded={open}>
      <span style={chevronStyle} aria-hidden="true">{open ? '▼' : '▶'}</span>
      <span style={titleStyle}>{block.title}</span>
    </button>
  );

  if (!open) {
    return (
      <div style={sectionStyle} data-coach="panel-design">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 8px' }}>
          {toggle}
          <span style={{ ...statusStyle, marginLeft: 'auto' }}>{status}</span>
        </div>
      </div>
    );
  }

  const renderSliders = (entries, { tagged = false } = {}) => (
    <div style={slidersStyle(tagged)}>
      {entries.map(([key, def]) => {
        const value = values[key] ?? def.default;
        const yours = !isDefaultValue(def, value);
        return (
          <React.Fragment key={key}>
            <span style={labelStyle}>{def.label || key}</span>
            <input
              type="range"
              min={def.min}
              max={def.max}
              step={def.step}
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              style={{ width: '100%', minWidth: 0, margin: 0, cursor: 'pointer' }}
              aria-label={def.label || key}
            />
            <ParamNumberBox def={def} value={value} onCommit={(n) => handleChange(key, n)} style={valueBoxStyle} />
            {tagged ? (
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px' }}>
                <span style={unitStyle}>{def.unit}</span>
                <span style={tagStyle(yours)} title={yours ? `BatPaC's value is ${def.default} ${def.unit}` : undefined}>
                  {yours ? 'yours' : 'BatPaC'}
                </span>
              </span>
            ) : (
              <span style={unitStyle}>{def.unit}</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const delta = (value, base) => {
    const pct = percentChange(value, base);
    if (pct === null || Math.abs(pct) < 0.5) return null;
    return (
      <span
        style={{ fontSize: '12px', fontWeight: 600, color: pct < 0 ? '#1e8e3e' : '#b06000', marginLeft: '6px' }}
        title="Against the default design"
      >
        {pct > 0 ? '+' : '−'}{Math.abs(pct).toFixed(0)}%
      </span>
    );
  };

  // The numbers the headline already shows are not repeated in the list.
  const listed = block.display.filter(d => d !== headline);
  const hasMore = groups.more.length > 0 || groups[MATERIAL].length > 0
    || groups[CELL].length > 0 || hasDrawing || listed.length > 0;

  const buttonStyle = (primary) => ({
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: primary ? 600 : 500,
    borderRadius: '4px',
    ...(primary
      ? { background: canApply ? '#1a73e8' : '#a0c4f1', color: '#fff', border: 'none', cursor: canApply ? 'pointer' : 'default' }
      : { background: '#fff', color: '#5a6b7d', border: '1px solid #c8ddf5', cursor: 'pointer' }),
  });

  return (
    <div style={sectionStyle} data-coach="panel-design">
      <div style={introStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {toggle}
          {(block.description || block.sources.length > 0 || block.notes.length > 0) && (
            <SourcesButton description={block.description} sources={block.sources} notes={block.notes} />
          )}
        </div>
        <div style={figuresStyle} aria-label="Design results">
          {headline && (
            <span>
              <span style={bigNumberStyle}>{formatValue(derived[headline.key])}</span>{' '}
              <span style={figureUnitStyle}>{headline.unit}</span>
            </span>
          )}
          {preview && (
            <>
              <span>
                <span style={bigNumberStyle}>{preview.carbon.toFixed(1)}</span>{' '}
                <span style={figureUnitStyle}>{carbonUnit}</span>
                {atDefaults && delta(preview.carbon, atDefaults.carbon)}
              </span>
              <span>
                <span style={bigNumberStyle}>{preview.cost.toFixed(1)}</span>{' '}
                <span style={figureUnitStyle}>{costUnit}</span>
                {atDefaults && delta(preview.cost, atDefaults.cost)}
              </span>
            </>
          )}
        </div>
      </div>

      <section style={cardStyle} aria-label="Design settings">
        {renderSliders(groups.primary)}
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <button onClick={handleApply} disabled={!canApply} style={buttonStyle(true)}>
            {canApply ? 'Apply' : 'Applied'}
          </button>
          <button onClick={handleReset} style={buttonStyle(false)}>Reset</button>
          {canApply && <span style={statusStyle}>{status}</span>}
          {hasMore && (
            <button
              type="button"
              style={{ ...toggleStyle, marginLeft: 'auto', fontSize: '12px', color: '#1a56a0', fontWeight: 500 }}
              onClick={() => setAllOpen(o => !o)}
              aria-expanded={allOpen}
            >
              All settings
              <span style={chevronStyle} aria-hidden="true">{allOpen ? '▼' : '▶'}</span>
            </button>
          )}
        </div>
      </section>

      {allOpen && (
        <div style={{ ...cardsStyle, marginTop: '12px' }}>
          {groups.more.length > 0 && (
            <section style={cardStyle} aria-label="Electrode">
              <h4 style={cardTitleStyle}>Electrode</h4>
              {renderSliders(groups.more)}
            </section>
          )}

          {groups[MATERIAL].length > 0 && (
            <section style={cardStyle} aria-label="Cathode material">
              <h4 style={cardTitleStyle}>Cathode material</h4>
              {renderSliders(groups[MATERIAL], { tagged: true })}
            </section>
          )}

          {(groups[CELL].length > 0 || hasDrawing) && (
            <section style={cardStyle} aria-label="Cell size">
              <h4 style={cardTitleStyle}>Cell size</h4>
              {renderSliders(groups[CELL])}
              {hasDrawing && (
                <div style={{ marginTop: '12px' }}>
                  <CellDrawing scope={scope} />
                </div>
              )}
            </section>
          )}

          {listed.length > 0 && (
            <section style={cardStyle} aria-label="Calculated quantities">
              <h4 style={cardTitleStyle}>Calculated{block.basis ? ` · ${block.basis}` : ''}</h4>
              {listed.map(({ key, label, unit, emphasis, sub }) => (
                <div key={key} style={sub ? { ...derivedRowStyle, paddingLeft: '14px', color: '#888' } : derivedRowStyle}>
                  <span>{label || key}</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: emphasis ? 600 : 400, whiteSpace: 'nowrap' }}>
                    {formatValue(derived[key])}{unit ? ` ${unit}` : ''}
                  </span>
                </div>
              ))}
            </section>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div style={warningStyle}>
          <strong>Formula error.</strong> {errors.map(e => `${e.key}: ${e.message}`).join('; ')}
        </div>
      )}

      {unresolved.length > 0 && (
        <div style={warningStyle}>
          <strong>Not linked to the inventory.</strong>{' '}
          {unresolved.map(u => `${u.process_id} (${u.reason})`).join('; ')}. Applying will skip these.
        </div>
      )}
    </div>
  );
};

export default DesignParametersPanel;
